/**
 * TypeScript view of the `crossword` format (ADR 0032).
 * Source of truth: `shared/schema/crossword.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "fr" | "en";
export type ClueLayout = "numbered" | "coordinates";
export type Direction = "across" | "down";
export type Cell = [row: number, col: number];

/** A clue is attached to its slot by direction and start cell, never by number. */
export interface Clue {
  direction: Direction;
  start: Cell;
  text: string;
}

export interface CrosswordDefinition {
  puzzleType: "crossword";
  typeVersion: number;
  language: Language;
  clueLayout: ClueLayout;
  /** One string per row, `#` for a block and `.` for a letter cell. Rectangular. */
  grid: string[];
  clues: Clue[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** A run of at least two letter cells, derived from the grid. Never stored. */
export interface Slot {
  direction: Direction;
  start: Cell;
  length: number;
  number: number;
}

export const BLOCK = "#";
export const EMPTY = ".";
export const MAX_SIZE = 30;
export const LANGUAGES: Language[] = ["fr", "en"];
export const LAYOUTS: ClueLayout[] = ["numbered", "coordinates"];

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface CrosswordProgress {
  version: 1;
  /** One string per row: an uppercase letter, `#` on a block, `.` for an empty cell. */
  rows: string[];
}

export interface CrosswordSubmission {
  rows: string[];
}

/** The author's letters, in the submission format. */
export type CrosswordSolution = CrosswordSubmission;

export function cellKey([r, c]: Cell): string {
  return `${r},${c}`;
}
